"use client";

import * as React from "react";
import { ArrowLeft, X, type LucideIcon } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { cn } from "@/lib/utils";

const containerSizes = {
  default: "h-11 w-11",
  sm: "h-9 w-9",
} as const;

const iconSizes = {
  default: "h-5 w-5",
  sm: "h-4 w-4",
} as const;

const labelSizes = {
  default: "text-lg font-semibold",
  sm: "text-base font-semibold",
} as const;

interface NavigationIconButtonProps {
  label?: string;
  icon?: LucideIcon;
  onClick?: (e?: React.MouseEvent) => void;
  href?: string;
  size?: "default" | "sm";
  className?: string;
  iconContainerClassName?: string;
  ariaLabel?: string;
  /** When no onClick/href is provided: "back" calls router.back(), "noop" does nothing */
  defaultAction?: "back" | "noop";
}

const NavigationIconButton = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  NavigationIconButtonProps
>(
  (
    {
      label,
      icon: Icon = ArrowLeft,
      onClick,
      href,
      size = "default",
      className,
      iconContainerClassName,
      ariaLabel,
      defaultAction = "back",
    },
    ref
  ) => {
    const router = useRouter();

    const handleClick = (e: React.MouseEvent) => {
      if (onClick) {
        onClick(e);
      } else if (defaultAction === "back") {
        router.back();
      }
    };

    const content = (
      <>
        <span
          className={cn(
            // Press-scale gives the icon button the same "tap heard" affordance
            // the base Button has. `transition` already covers transform; pin the
            // signature ease + a reduced-motion guard (no global reset on web).
            "flex shrink-0 items-center justify-center rounded-full bg-foreground/10 text-foreground transition duration-150 ease-signature hover:bg-foreground/15 active:scale-90 motion-reduce:transition-none motion-reduce:active:scale-100",
            containerSizes[size],
            iconContainerClassName
          )}
        >
          <Icon className={iconSizes[size]} />
        </span>
        {label && (
          <span className={cn("text-foreground", labelSizes[size])}>
            {label}
          </span>
        )}
      </>
    );

    const wrapperClassName = cn("inline-flex items-center gap-3", className);
    const resolvedAriaLabel = ariaLabel ?? label ?? "Go back";

    if (href) {
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          className={wrapperClassName}
          aria-label={resolvedAriaLabel}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        type="button"
        ref={ref as React.Ref<HTMLButtonElement>}
        onClick={handleClick}
        className={wrapperClassName}
        aria-label={resolvedAriaLabel}
      >
        {content}
      </button>
    );
  }
);
NavigationIconButton.displayName = "NavigationIconButton";

export const BackButton = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  Omit<NavigationIconButtonProps, "icon"> & { icon?: LucideIcon }
>((props, ref) => {
  return <NavigationIconButton ref={ref} {...props} />;
});
BackButton.displayName = "BackButton";

export const CloseButton = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  Omit<NavigationIconButtonProps, "icon" | "defaultAction"> & {
    icon?: LucideIcon;
  }
>((props, ref) => {
  const ariaLabel = props.ariaLabel ?? "Close";
  return (
    <NavigationIconButton
      ref={ref}
      icon={X}
      defaultAction="noop"
      {...props}
      ariaLabel={ariaLabel}
    />
  );
});
CloseButton.displayName = "CloseButton";
